import {
  Archive,
  AudioLines,
  ChevronDown,
  ChevronRight,
  createIcons,
  Database,
  Download,
  History,
  Lightbulb,
  MessageCircle,
  Mic,
  NotebookTabs,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Settings2,
  Trash2,
  TriangleAlert,
  Upload,
  Volume2,
  VolumeX,
  X,
} from "lucide";
import "./styles.css";
import { AudioRecorder } from "./audio-recorder.js";
import { PROMPT_LABELS } from "./content.js";
import { exportLocalBackup, exportQuestionBank } from "./exporter.js";
import {
  GatewayClient,
  isAudioPlaybackStoppedError,
  playAudioBlob,
  primeAudioPlayback,
  stopAudioPlayback,
} from "./gateway.js";
import { PracticeEngine } from "./practice-engine.js";
import { feedbackForAnswer, normalizeStringList } from "./result-utils.js";
import {
  clearAudio,
  clearHistory,
  deleteCard,
  deleteSession,
  factoryReset,
  getAccessToken,
  getAudio,
  getPreferences,
  getPrompts,
  getStorageSummary,
  initializeStorage,
  listCards,
  listSessions,
  pruneExpiredSessions,
  resetPrompts,
  resetQuestionBank,
  saveAudio,
  saveCard,
  saveCards,
  savePreferences,
  savePrompts,
  setCardEnabled,
} from "./storage.js";

const elements = {
  viewCaption: document.querySelector("#view-caption"),
  connectionStatus: document.querySelector("#connection-status"),
  openSettings: document.querySelector("#open-settings"),
  bottomNav: document.querySelector("#bottom-nav"),
  views: [...document.querySelectorAll(".app-view")],
  dailyStyle: document.querySelector("#daily-style"),
  conversation: document.querySelector("#conversation"),
  conversationEmpty: document.querySelector("#conversation-empty"),
  dailyRecord: document.querySelector("#daily-record"),
  dailyRecordLabel: document.querySelector("#daily-record-label"),
  dailyRecordTime: document.querySelector("#daily-record-time"),
  dailyLevel: document.querySelector("#daily-level"),
  ieltsSetup: document.querySelector("#ielts-setup"),
  ieltsPractice: document.querySelector("#ielts-practice"),
  ieltsResult: document.querySelector("#ielts-result"),
  ieltsMode: document.querySelector("#ielts-mode"),
  cardSourceRow: document.querySelector("#card-source-row"),
  cardSource: document.querySelector("#card-source"),
  startIelts: document.querySelector("#start-ielts"),
  practicePart: document.querySelector("#practice-part"),
  practiceProgress: document.querySelector("#practice-progress"),
  cueCard: document.querySelector("#cue-card"),
  cueTitle: document.querySelector("#cue-title"),
  cuePoints: document.querySelector("#cue-points"),
  prepCountdown: document.querySelector("#prep-countdown"),
  countdownValue: document.querySelector("#countdown-value"),
  countdownProgress: document.querySelector("#countdown-progress"),
  skipCountdown: document.querySelector("#skip-countdown"),
  questionStage: document.querySelector("#question-stage"),
  questionSource: document.querySelector("#question-source"),
  questionText: document.querySelector("#question-text"),
  playQuestion: document.querySelector("#play-question"),
  requestHelp: document.querySelector("#request-help"),
  helpSheet: document.querySelector("#help-sheet"),
  helpContent: document.querySelector("#help-content"),
  closeHelp: document.querySelector("#close-help"),
  answerLog: document.querySelector("#answer-log"),
  examRecordDock: document.querySelector("#exam-record-dock"),
  examRecord: document.querySelector("#exam-record"),
  examRecordLabel: document.querySelector("#exam-record-label"),
  examRecordTime: document.querySelector("#exam-record-time"),
  examLevel: document.querySelector("#exam-level"),
  stopPractice: document.querySelector("#stop-practice"),
  overallBand: document.querySelector("#overall-band"),
  scoreGrid: document.querySelector("#score-grid"),
  scoreReasons: document.querySelector("#score-reasons"),
  scoreSuggestions: document.querySelector("#score-suggestions"),
  improvedExamples: document.querySelector("#improved-examples"),
  timingGrid: document.querySelector("#timing-grid"),
  resultCardReview: document.querySelector("#result-card-review"),
  resultTurns: document.querySelector("#result-turns"),
  startPart3: document.querySelector("#start-part3"),
  restartIelts: document.querySelector("#restart-ielts"),
  historyFilter: document.querySelector("#history-filter"),
  historyList: document.querySelector("#history-list"),
  serviceCopy: document.querySelector("#service-copy"),
  activationForm: document.querySelector("#activation-form"),
  activationCode: document.querySelector("#activation-code"),
  logout: document.querySelector("#logout"),
  textProvider: document.querySelector("#text-provider"),
  audioProvider: document.querySelector("#audio-provider"),
  transcriptionProvider: document.querySelector("#transcription-provider"),
  speechProvider: document.querySelector("#speech-provider"),
  saveRecordings: document.querySelector("#save-recordings"),
  retentionDays: document.querySelector("#retention-days"),
  refreshStorage: document.querySelector("#refresh-storage"),
  storageStats: document.querySelector("#storage-stats"),
  exportBackup: document.querySelector("#export-backup"),
  clearAudio: document.querySelector("#clear-audio"),
  clearHistory: document.querySelector("#clear-history"),
  factoryReset: document.querySelector("#factory-reset"),
  promptsForm: document.querySelector("#prompts-form"),
  promptFields: document.querySelector("#prompt-fields"),
  resetPrompts: document.querySelector("#reset-prompts"),
  bankCount: document.querySelector("#bank-count"),
  bankSearch: document.querySelector("#bank-search"),
  addCard: document.querySelector("#add-card"),
  importBank: document.querySelector("#import-bank"),
  exportBank: document.querySelector("#export-bank"),
  resetBank: document.querySelector("#reset-bank"),
  bankFile: document.querySelector("#bank-file"),
  bankList: document.querySelector("#bank-list"),
  confirmDialog: document.querySelector("#confirm-dialog"),
  confirmTitle: document.querySelector("#confirm-title"),
  confirmMessage: document.querySelector("#confirm-message"),
  confirmAction: document.querySelector("#confirm-action"),
  cardDialog: document.querySelector("#card-dialog"),
  cardForm: document.querySelector("#card-form"),
  cardDialogTitle: document.querySelector("#card-dialog-title"),
  closeCardDialog: document.querySelector("#close-card-dialog"),
  cancelCard: document.querySelector("#cancel-card"),
  cardId: document.querySelector("#card-id"),
  cardTitle: document.querySelector("#card-title"),
  cardPoints: document.querySelector("#card-points"),
  cardRounding: document.querySelector("#card-rounding"),
  cardTopic: document.querySelector("#card-topic"),
  cardQuestions: document.querySelector("#card-questions"),
  cardCategory: document.querySelector("#card-category"),
  busyOverlay: document.querySelector("#busy-overlay"),
  busyCopy: document.querySelector("#busy-copy"),
  toastRegion: document.querySelector("#toast-region"),
};

const gateway = new GatewayClient();
const engine = new PracticeEngine({ gateway });
const recorder = new AudioRecorder({
  onLevel: (level) => updateRecordingLevel(level),
  onElapsed: (seconds) => updateRecordingTime(seconds),
  onLimit: () => finishRecording().catch(showError),
});

let activeView = "talk";
let dailyStyle = "standard";
let ieltsMode = "part1";
let cardSource = "bank";
let recordPurpose = "";
let currentQuestionAudio = null;
let questionPresentationId = 0;
let countdownTimer = null;
let historyFilter = "all";
let gatewayReady = false;
let gatewayNeedsActivation = true;
let capabilities = null;
const sessionLookup = new Map();
const speechAudioCache = new Map();
const MAX_SPEECH_CACHE_ENTRIES = 24;
let speechCacheGeneration = 0;

const APP_ICONS = {
  Archive,
  AudioLines,
  ChevronDown,
  ChevronRight,
  Database,
  Download,
  History,
  Lightbulb,
  MessageCircle,
  Mic,
  NotebookTabs,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Settings2,
  Trash2,
  TriangleAlert,
  Upload,
  Volume2,
  VolumeX,
  X,
};

/**
 * 初始化本地数据、交互、服务连接和可恢复练习。
 *
 * @returns {Promise<void>} 页面初始化完成结果。
 */
async function initialize() {
  await initializeStorage();
  bindEvents();
  bindEngineEvents();
  refreshIcons();
  const preferences = await getPreferences();
  populatePreferences(preferences);
  await pruneExpiredSessions(preferences.retentionDays);
  await Promise.all([renderPrompts(), renderQuestionBank(), renderStorageSummary(), renderHistory(), renderDailyConversation()]);
  await checkGateway();
  if (await engine.restoreActiveState()) {
    switchView("ielts");
    showPracticeView();
    renderRestoredAnswers(engine.answers);
  }
  registerServiceWorker();
}

/**
 * 注册页面和数据管理交互。
 *
 * @returns {void}
 */
function bindEvents() {
  elements.bottomNav.addEventListener("click", handleNavigation);
  elements.openSettings.addEventListener("click", () => switchView("data"));
  elements.dailyStyle.addEventListener("click", handleDailyStyleChange);
  elements.dailyRecord.addEventListener("click", () => handleRecordingButton("daily"));
  elements.conversation.addEventListener("click", handleSessionContentAction);
  elements.examRecord.addEventListener("click", () => handleRecordingButton("exam"));
  elements.ieltsMode.addEventListener("click", handleIeltsModeChange);
  elements.cardSource.addEventListener("click", (event) => {
    const button = event.target.closest("button[data-value]");
    if (!button) return;
    cardSource = button.dataset.value;
    selectSegment(elements.cardSource, cardSource);
  });
  elements.startIelts.addEventListener("click", startIeltsFromUserGesture);
  elements.skipCountdown.addEventListener("click", finishCountdown);
  elements.playQuestion.addEventListener("click", replayCurrentQuestion);
  elements.requestHelp.addEventListener("click", requestAnswerHelp);
  elements.closeHelp.addEventListener("click", () => { elements.helpSheet.hidden = true; });
  elements.stopPractice.addEventListener("click", stopPractice);
  elements.restartIelts.addEventListener("click", resetIeltsView);
  elements.startPart3.addEventListener("click", startPart3FromResult);
  elements.ieltsResult.addEventListener("click", handleSessionContentAction);
  elements.historyFilter.addEventListener("click", handleHistoryFilter);
  elements.historyList.addEventListener("click", handleHistoryAction);
  elements.activationForm.addEventListener("submit", activateGateway);
  elements.logout.addEventListener("click", logoutGateway);
  [elements.textProvider, elements.audioProvider, elements.transcriptionProvider, elements.speechProvider].forEach((select) => select.addEventListener("change", saveProviderPreferences));
  elements.saveRecordings.addEventListener("change", saveLocalPreferences);
  elements.retentionDays.addEventListener("change", saveLocalPreferences);
  elements.refreshStorage.addEventListener("click", renderStorageSummary);
  elements.exportBackup.addEventListener("click", () => runBusy("正在生成备份", exportLocalBackup));
  elements.clearAudio.addEventListener("click", clearAllAudio);
  elements.clearHistory.addEventListener("click", clearAllHistory);
  elements.factoryReset.addEventListener("click", resetAllLocalData);
  elements.promptsForm.addEventListener("submit", savePromptEditor);
  elements.resetPrompts.addEventListener("click", resetPromptEditor);
  elements.bankSearch.addEventListener("input", renderQuestionBank);
  elements.addCard.addEventListener("click", () => openCardEditor());
  elements.importBank.addEventListener("click", () => elements.bankFile.click());
  elements.bankFile.addEventListener("change", importQuestionBank);
  elements.exportBank.addEventListener("click", async () => exportQuestionBank(await listCards()));
  elements.resetBank.addEventListener("click", restoreQuestionBank);
  elements.bankList.addEventListener("click", handleCardAction);
  elements.cardForm.addEventListener("submit", submitCardEditor);
  elements.closeCardDialog.addEventListener("click", closeCardEditor);
  elements.cancelCard.addEventListener("click", closeCardEditor);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", releaseRecorderResources);
}

/**
 * 绑定客户端练习状态机向界面发出的事件。
 *
 * @returns {void}
 */
function bindEngineEvents() {
  engine.addEventListener("state", (event) => {
    const labels = { preparing: "正在准备练习", analyzing: "正在分析并生成回复", transcribing: "正在识别回答", scoring: "正在生成评分", ready: "" };
    const label = labels[event.detail.state];
    if (label) showBusy(label);
    else hideBusy();
  });
  engine.addEventListener("daily-ready", hideBusy);
  engine.addEventListener("daily-result", (event) => {
    hideBusy();
    appendConversationTurn(event.detail.result, event.detail.session);
    prefetchSessionSpeech({ ...event.detail.session, result: event.detail.result });
    if (event.detail.assistantAudio) playDailyReply(event.detail.assistantAudio);
    renderHistory();
    renderStorageSummary();
  });
  engine.addEventListener("part-started", (event) => {
    elements.practicePart.textContent = event.detail.part.toUpperCase();
    elements.practiceProgress.textContent = `1 / ${event.detail.total}`;
  });
  engine.addEventListener("part2-card", (event) => {
    hideBusy();
    showPracticeView();
    renderCueCard(event.detail.card);
    startCountdown(event.detail.preparationSeconds);
  });
  engine.addEventListener("question", (event) => {
    hideBusy();
    presentExamQuestion(event.detail);
  });
  engine.addEventListener("transcript", (event) => renderAnswer(event.detail.answer));
  engine.addEventListener("help", (event) => { hideBusy(); renderHelp(event.detail.content); });
  engine.addEventListener("assessment", (event) => {
    hideBusy();
    renderAssessment(event.detail);
    renderHistory();
    renderStorageSummary();
  });
  engine.addEventListener("fallback", (event) => showToast(event.detail.message));
  engine.addEventListener("speech-degraded", (event) => showToast(event.detail.message));
}

/**
 * 重新渲染页面中新增的 Lucide 图标。
 *
 * @returns {void}
 */
function refreshIcons() {
  createIcons({ icons: APP_ICONS, attrs: { "aria-hidden": "true" } });
}

/**
 * 处理底部导航点击。
 *
 * @param {MouseEvent} event 点击事件。
 * @returns {void}
 */
function handleNavigation(event) {
  const button = event.target.closest("button[data-view]");
  if (button) switchView(button.dataset.view);
}

/**
 * 切换主视图并按需刷新本地数据。
 *
 * @param {"talk"|"ielts"|"history"|"data"} view 目标视图。
 * @returns {void}
 */
function switchView(view) {
  const captions = { talk: "日常对话", ielts: "IELTS 练习", history: "练习历史", data: "设置与数据" };
  if (activeView !== view) {
    questionPresentationId += 1;
    stopAudioPlayback();
  }
  activeView = view;
  elements.views.forEach((item) => { item.hidden = item.dataset.view !== view; item.classList.toggle("is-active", item.dataset.view === view); });
  elements.bottomNav.querySelectorAll("button").forEach((button) => button.classList.toggle("is-selected", button.dataset.view === view));
  elements.viewCaption.textContent = captions[view];
  if (view === "history") renderHistory();
  if (view === "data") Promise.all([renderStorageSummary(), renderQuestionBank()]);
  window.scrollTo({ top: 0, behavior: "smooth" });
}

/**
 * 选中一个分段控件按钮。
 *
 * @param {HTMLElement} container 分段控件。
 * @param {string} value 目标值。
 * @returns {void}
 */
function selectSegment(container, value) {
  container.querySelectorAll("button[data-value]").forEach((button) => button.classList.toggle("is-selected", button.dataset.value === value));
}

/**
 * 检查本机令牌及网关可用性。
 *
 * @returns {Promise<boolean>} 网关是否可用。
 */
async function checkGateway() {
  if (!await getAccessToken()) {
    gatewayNeedsActivation = true;
    setGatewayState(false, "未激活");
    return false;
  }
  if (!navigator.onLine) {
    gatewayNeedsActivation = false;
    setGatewayState(false, "连接失败");
    return false;
  }
  try {
    capabilities = await gateway.bootstrap();
    gatewayNeedsActivation = false;
    setGatewayState(true, "已连接");
    await applyCapabilities(capabilities);
    return true;
  } catch (error) {
    gatewayNeedsActivation = error.status === 401;
    setGatewayState(false, error.status === 401 ? "需要重新激活" : "连接失败");
    return false;
  }
}

/**
 * 更新顶部状态点和激活表单。
 *
 * @param {boolean} ready 是否已连接。
 * @param {string} label 设置页状态文字。
 * @returns {void}
 */
function setGatewayState(ready, label) {
  gatewayReady = ready;
  elements.connectionStatus.dataset.state = ready ? "ready" : "offline";
  elements.serviceCopy.textContent = label;
  elements.activationForm.hidden = ready;
  elements.logout.hidden = !ready;
}

/**
 * 根据服务端公开能力禁用未配置的服务商选项。
 *
 * @param {object} serviceCapabilities 网关能力信息。
 * @returns {Promise<void>} 服务商选项和本地偏好同步结果。
 */
async function applyCapabilities(serviceCapabilities) {
  const providers = serviceCapabilities.providers || {};
  const providerFields = [
    ["textProvider", elements.textProvider],
    ["audioProvider", elements.audioProvider],
    ["transcriptionProvider", elements.transcriptionProvider],
    ["speechProvider", elements.speechProvider],
  ];
  const preferenceUpdates = {};
  let missingProviderGroup = false;

  // 每类能力独立选择第一个可用服务商，并保存自动降级后的设置。
  for (const [preferenceKey, select] of providerFields) {
    const options = [...select.options];
    options.forEach((option) => { option.disabled = !providers[option.value]; });
    const available = options.find((option) => !option.disabled);
    select.disabled = !available;
    if (!available) {
      missingProviderGroup = true;
      continue;
    }
    if (select.selectedOptions[0]?.disabled) {
      select.value = available.value;
      preferenceUpdates[preferenceKey] = available.value;
    }
  }
  if (Object.keys(preferenceUpdates).length) await savePreferences(preferenceUpdates);
  if (missingProviderGroup) showToast("当前没有完整可用的模型服务，请检查服务配置", true);
}

/**
 * 使用表单激活单用户网关。
 *
 * @param {SubmitEvent} event 表单提交事件。
 * @returns {Promise<void>} 激活完成结果。
 */
async function activateGateway(event) {
  event.preventDefault();
  try {
    showBusy("正在激活");
    capabilities = await gateway.activate(elements.activationCode.value);
    elements.activationCode.value = "";
    gatewayNeedsActivation = false;
    setGatewayState(true, "已连接");
    await applyCapabilities(capabilities);
    showToast("这台 iPhone 已激活");
  } catch (error) {
    showError(error);
  } finally {
    hideBusy();
  }
}

/**
 * 清除本机网关令牌但保留练习数据。
 *
 * @returns {Promise<void>} 退出完成结果。
 */
async function logoutGateway() {
  if (!await confirmAction("清除激活信息", "题库和练习数据会保留，下次使用模型前需要重新输入激活码。", "清除")) return;
  await gateway.logout();
  gatewayNeedsActivation = true;
  setGatewayState(false, "未激活");
  showToast("已清除本机激活信息");
}

/**
 * 验证服务已激活，否则切到设置页。
 *
 * @returns {boolean} 是否可以继续模型操作。
 */
function requireGateway() {
  if (gatewayReady) return true;
  switchView("data");
  showToast(gatewayNeedsActivation ? "请先输入激活码" : "服务连接失败，请检查网络后重试", true);
  return false;
}

/**
 * 在用户点击录音按钮时预激活 iOS 音频，再切换录音状态。
 *
 * @param {"daily"|"exam"} purpose 当前录音用途。
 * @returns {Promise<void>} 播放授权和录音状态切换结果。
 */
async function handleRecordingButton(purpose) {
  await primeAudioPlayback();
  await toggleRecording(purpose);
}

/**
 * 自动播放本轮日常对话的模型回复，失败时保留卡片中的手动发音入口。
 *
 * @param {Blob} audio 模型回复语音。
 * @returns {Promise<void>} 自动播放尝试结果。
 */
async function playDailyReply(audio) {
  try {
    await playAppAudio(audio);
  } catch (error) {
    if (!isAudioPlaybackStoppedError(error)) showToast("未能自动播放回复，请点按“发音”重播", true);
  }
}

/**
 * 播放应用语音前释放已停止录音的旧输入流，避免 iOS 输出通道切换后留下无采样麦克风。
 *
 * @param {Blob} audio 待播放的语音内容。
 * @returns {Promise<void>} 播放完成或失败结果。
 */
async function playAppAudio(audio) {
  if (recorder.isRecording()) throw new Error("请先结束录音再播放语音");
  recorder.releaseReusableStream();
  await playAudioBlob(audio);
}

/**
 * 开始或结束日常/IELTS 录音。
 *
 * @param {"daily"|"exam"} purpose 当前录音用途。
 * @returns {Promise<void>} 录音状态切换结果。
 */
async function toggleRecording(purpose) {
  if (recorder.isStarting()) return;
  if (recorder.isRecording()) {
    await finishRecording();
    return;
  }
  if (!requireGateway()) return;
  try {
    stopAudioPlayback();
    if (purpose === "daily" && (!engine.active || engine.mode !== "daily")) await engine.start("daily", { style: dailyStyle });
    if (purpose === "exam" && !engine.currentQuestion) throw new Error("请先开始 IELTS 练习");
    recordPurpose = purpose;
    await recorder.start();
    if (!recorder.isRecording()) return;
    setRecordingUi(true);
  } catch (error) {
    recorder.cancel();
    recordPurpose = purpose;
    setRecordingUi(false);
    recordPurpose = "";
    showError(error);
  }
}

/**
 * 完成当前录音并提交给对应练习流程。
 *
 * @returns {Promise<void>} 当前轮处理结果。
 */
async function finishRecording() {
  if (!recorder.isRecording()) return;
  try {
    const recording = await recorder.stop();
    setRecordingUi(false);
    if (recordPurpose === "daily") await engine.submitDaily(recording);
    else await engine.submitExamAnswer(recording);
  } catch (error) {
    setRecordingUi(false);
    hideBusy();
    showError(error);
  } finally {
    recordPurpose = "";
  }
}

/**
 * 更新录音按钮、状态文案和计时器初始值。
 *
 * @param {boolean} recording 是否正在录音。
 * @returns {void}
 */
function setRecordingUi(recording) {
  const button = recordPurpose === "exam" ? elements.examRecord : elements.dailyRecord;
  const label = recordPurpose === "exam" ? elements.examRecordLabel : elements.dailyRecordLabel;
  button.classList.toggle("is-recording", recording);
  button.setAttribute("aria-label", recording ? "结束录音" : "开始录音");
  label.textContent = recording ? "正在录音，点按结束" : (recordPurpose === "exam" ? "点按回答" : "点按开始发言");
  if (recordPurpose === "exam") elements.playQuestion.disabled = recording;
  updateRecordingTime(0);
}

/**
 * 更新当前录音的音量条。
 *
 * @param {number} level AudioWorklet RMS。
 * @returns {void}
 */
function updateRecordingLevel(level) {
  const target = recordPurpose === "exam" ? elements.examLevel : elements.dailyLevel;
  target.style.width = `${recordingLevelPercent(level)}%`;
}

/**
 * 将麦克风 RMS 映射为更适合手机端观察的非线性音浪宽度。
 *
 * @param {number} level AudioWorklet 返回的归一化 RMS。
 * @returns {number} 4 到 100 之间的显示百分比。
 */
function recordingLevelPercent(level) {
  const normalized = Math.max(0, Number(level) || 0);
  if (normalized === 0) return 4;
  return Math.max(4, Math.min(100, Math.sqrt(normalized) * 360));
}

/**
 * 更新当前录音的分秒显示。
 *
 * @param {number} seconds 已录制秒数。
 * @returns {void}
 */
function updateRecordingTime(seconds) {
  const formatted = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
  (recordPurpose === "exam" ? elements.examRecordTime : elements.dailyRecordTime).textContent = formatted;
}

/**
 * 按时间倒序恢复最近一百轮日常对话，最近三轮默认展开。
 *
 * @returns {Promise<void>} 对话历史渲染完成结果。
 */
async function renderDailyConversation() {
  const sessions = (await listSessions(Number.MAX_SAFE_INTEGER)).filter((session) => session.kind === "daily").slice(0, 100);
  elements.conversation.querySelectorAll(".conversation-turn").forEach((item) => item.remove());
  elements.conversationEmpty.hidden = sessions.length > 0;
  sessions.forEach((session) => sessionLookup.set(session.id, session));
  elements.conversationEmpty.insertAdjacentHTML("afterend", sessions.map((session, index) => dailyTurnMarkup(session, index < 3)).join(""));
  refreshIcons();
  elements.conversation.scrollTop = 0;
}

/**
 * 将一轮日常结果追加到当前对话视图。
 *
 * @param {object} result 模型对话结果。
 * @param {object} session 本地历史记录。
 * @returns {void}
 */
function appendConversationTurn(result, session) {
  const savedSession = { ...session, result };
  sessionLookup.set(session.id, savedSession);
  elements.conversationEmpty.hidden = true;
  elements.conversationEmpty.insertAdjacentHTML("afterend", dailyTurnMarkup(savedSession, true));
  const items = elements.conversation.querySelectorAll(".conversation-turn");
  items.forEach((item, index) => { item.open = index < 3; });
  if (items.length > 100) items[items.length - 1].remove();
  refreshIcons();
  elements.conversation.scrollTo({ top: 0, behavior: "smooth" });
}

/**
 * 生成一轮日常对话的完整、安全展示结构。
 *
 * @param {object} session 本地日常会话。
 * @param {boolean} open 是否默认展开。
 * @returns {string} 日常对话 HTML。
 */
function dailyTurnMarkup(session, open = false) {
  return `<details class="conversation-turn" data-session-id="${escapeHtml(session.id)}"${open ? " open" : ""}>
    <summary><span>${escapeHtml(session.result?.recognized_text || "本轮语音")}</span><time>${formatTime(session.createdAt)}</time></summary>
    ${dailyTurnBodyMarkup(session)}
  </details>`;
}

/**
 * 生成日常对话卡片的展开内容，供对话页和历史页复用。
 *
 * @param {object} session 本地日常会话。
 * @returns {string} 日常对话详情 HTML。
 */
function dailyTurnBodyMarkup(session) {
  const result = session.result || {};
  const scores = result.scores || {};
  const reasons = normalizeStringList(result.score_reasons_zh);
  const suggestions = normalizeStringList(result.suggestions_zh);
  const userAudioId = session.audioIds?.includes(`${session.id}-user`) ? `${session.id}-user` : "";
  return `<div class="turn-body">
      <section class="utterance"><div class="content-heading"><strong>你说</strong><div class="inline-actions">${userAudioId ? audioButton(userAudioId, "原声") : ""}<button class="mini-button" type="button" data-action="correct-daily"><i data-lucide="refresh-cw"></i>纠正</button></div></div><p class="recognized">${escapeHtml(result.recognized_text || "未识别到文本")}</p><div class="correction-slot"></div></section>
      <section class="utterance"><div class="content-heading"><strong>模型回复</strong><button class="mini-button" type="button" data-action="speak-daily-reply"><i data-lucide="volume-2"></i>发音</button></div><p class="reply">${escapeHtml(result.assistant_reply || "")}</p></section>
      <section class="utterance"><div class="content-heading"><strong>评分信息</strong></div><div class="score-strip">${scoreCell("总体", scores.overall)}${scoreCell("发音", scores.pronunciation)}${scoreCell("流利", scores.fluency)}${scoreCell("词汇", scores.vocabulary)}${scoreCell("语法", scores.grammar)}</div><div class="feedback-block"><h3>评分理由</h3>${renderStringList(reasons, "feedback-list") || "<p>本轮没有详细评分理由。</p>"}</div></section>
      <section class="utterance"><div class="content-heading"><strong>调整建议</strong></div>${renderStringList(suggestions, "feedback-list") || "<p>继续保持完整句作答。</p>"}${result.improved_expression ? `<div class="improved-box"><div class="content-heading"><strong>推荐表达</strong><button class="mini-button" type="button" data-action="speak-daily-improved"><i data-lucide="volume-2"></i>发音</button></div><p>${escapeHtml(result.improved_expression)}</p></div>` : ""}</section>
    </div>`;
}

/**
 * 处理日常、IELTS 结果和历史详情中的音频及纠正按钮。
 *
 * @param {MouseEvent} event 点击事件。
 * @returns {Promise<void>} 操作完成结果。
 */
async function handleSessionContentAction(event) {
  const sessionId = event.target.closest("[data-session-id]")?.dataset.sessionId;
  const session = sessionLookup.get(sessionId);
  const summary = event.target.closest("summary");
  if (summary && session) {
    const turnCard = summary.closest(".exam-turn-card[data-answer-index]");
    prefetchSessionSpeech(session, turnCard ? Number(turnCard.dataset.answerIndex) : null);
  }
  const button = event.target.closest("button[data-action]");
  if (!button) return;
  if (button.dataset.action === "play-audio") {
    await playStoredAudio(button.dataset.audioId);
    return;
  }
  if (!session) return;
  if (button.dataset.action === "correct-daily") await correctDailyTranscript(button, session);
  if (button.dataset.action === "speak-daily-reply") await playSavedOrSynthesizedText(session, `${session.id}-assistant`, session.result?.assistant_reply, session.style || "neutral");
  if (button.dataset.action === "speak-daily-improved") {
    await synthesizeText(session.result?.improved_expression, session.style || "neutral", { audioId: `${session.id}-daily-improved`, sessionId: session.id });
  }
  if (button.dataset.action === "speak-ielts-improved") {
    const answerIndex = Number(button.dataset.answerIndex);
    const feedback = feedbackForAnswer(session.result, answerIndex);
    await synthesizeText(feedback.improved_answer, "neutral", { audioId: `${session.id}-ielts-improved-${answerIndex}`, sessionId: session.id });
  }
}

/**
 * 请求模型纠正一轮转写，并把结果显示在当前卡片内。
 *
 * @param {HTMLButtonElement} button 触发纠正的按钮。
 * @param {object} session 当前日常会话。
 * @returns {Promise<void>} 纠正完成结果。
 */
async function correctDailyTranscript(button, session) {
  if (!requireGateway()) return;
  button.disabled = true;
  try {
    const [preferences, prompts] = await Promise.all([getPreferences(), getPrompts()]);
    const correction = await gateway.runAI("correct", { provider: preferences.textProvider, prompts, text: session.result?.recognized_text || "" });
    const changes = normalizeStringList(correction.changes_zh);
    const slot = button.closest(".utterance")?.querySelector(".correction-slot");
    if (slot) slot.innerHTML = `<div class="correction-box"><strong>纠正后</strong><p>${escapeHtml(correction.corrected_text || session.result?.recognized_text || "")}</p>${renderStringList(changes)}</div>`;
  } catch (error) {
    showError(error);
  } finally {
    button.disabled = false;
  }
}

/**
 * 播放一段本机保存的录音。
 *
 * @param {string} audioId 本地录音 ID。
 * @returns {Promise<void>} 播放完成结果。
 */
async function playStoredAudio(audioId) {
  const record = await getAudio(audioId);
  if (!record?.blob) return showToast("录音已经被删除", true);
  try {
    await playAppAudio(record.blob);
  } catch (error) {
    if (!isAudioPlaybackStoppedError(error)) showError(error);
  }
}

/**
 * 优先播放保存的模型语音，不存在时重新合成。
 *
 * @param {object} session 当前会话。
 * @param {string} audioId 候选本地录音 ID。
 * @param {string} speech 需要朗读的文本。
 * @param {string} style 语音风格。
 * @returns {Promise<void>} 播放完成结果。
 */
async function playSavedOrSynthesizedText(session, audioId, speech, style = "neutral") {
  if (session.audioIds?.includes(audioId)) {
    await playStoredAudio(audioId);
    return;
  }
  await synthesizeText(speech, style, { audioId, sessionId: session.id });
}

/**
 * 使用当前语音服务朗读一段文本。
 *
 * @param {string} speech 需要朗读的文本。
 * @param {string} style neutral 或 american。
 * @param {{audioId?:string, sessionId?:string}} cache 可选本机音频缓存标识。
 * @returns {Promise<void>} 合成并播放结果。
 */
async function synthesizeText(speech, style = "neutral", cache = {}) {
  if (!speech || !requireGateway()) return;
  try {
    await playAppAudio(await getSynthesizedSpeech(speech, style, cache));
  } catch (error) {
    if (!isAudioPlaybackStoppedError(error)) showError(error);
  }
}

/**
 * 优先读取本机或内存缓存，不存在时请求并保存模型语音。
 *
 * @param {string} speech 需要朗读的文本。
 * @param {string} style neutral 或 american。
 * @param {{audioId?:string, sessionId?:string}} cache 可选本机音频缓存标识。
 * @returns {Promise<Blob>} 可直接播放的语音。
 */
async function getSynthesizedSpeech(speech, style = "neutral", cache = {}) {
  const preferences = await getPreferences();
  const cacheKey = `${preferences.speechProvider}\n${style}\n${cache.audioId || ""}\n${speech}`;
  if (speechAudioCache.has(cacheKey)) return speechAudioCache.get(cacheKey);
  if (speechAudioCache.size >= MAX_SPEECH_CACHE_ENTRIES) speechAudioCache.delete(speechAudioCache.keys().next().value);
  const cacheGeneration = speechCacheGeneration;
  const audioPromise = (async () => {
    if (cache.audioId) {
      const saved = await getAudio(cache.audioId);
      if (saved?.blob) return saved.blob;
    }
    const audio = await gateway.synthesize(speech, preferences.speechProvider, style);
    if (cacheGeneration === speechCacheGeneration && preferences.saveRecordings && cache.audioId && cache.sessionId) {
      await saveAudio({ id: cache.audioId, sessionId: cache.sessionId, kind: "reference", mimeType: audio.type, blob: audio });
    }
    return audio;
  })();
  speechAudioCache.set(cacheKey, audioPromise);
  try {
    return await audioPromise;
  } catch (error) {
    speechAudioCache.delete(cacheKey);
    throw error;
  }
}

/**
 * 在后台准备一段参考表达语音，失败时仍保留点击后重试能力。
 *
 * @param {string} speech 需要预取的文本。
 * @param {string} style neutral 或 american。
 * @param {{audioId?:string, sessionId?:string}} cache 可选本机音频缓存标识。
 * @returns {void}
 */
function prefetchSpeech(speech, style = "neutral", cache = {}) {
  if (!speech || !gatewayReady) return;
  getSynthesizedSpeech(speech, style, cache).catch(() => {});
}

/**
 * 清空参考表达的内存缓存，并阻止尚未完成的预取重新写入本机。
 *
 * @returns {void}
 */
function clearSpeechAudioCache() {
  speechCacheGeneration += 1;
  speechAudioCache.clear();
}

/**
 * 预取日常推荐表达或 IELTS 当前可见轮次的参考表达。
 *
 * @param {object} session 当前练习记录。
 * @param {number|null} answerIndex 指定 IELTS 轮次；空值时预取前两轮。
 * @returns {void}
 */
function prefetchSessionSpeech(session, answerIndex = null) {
  if (!session?.id) return;
  if (session.kind === "daily") {
    prefetchSpeech(session.result?.improved_expression, session.style || "neutral", { audioId: `${session.id}-daily-improved`, sessionId: session.id });
    return;
  }
  const indexes = Number.isInteger(answerIndex) ? [answerIndex] : [0, 1];
  indexes.forEach((index) => {
    if (!session.answers?.[index]) return;
    const feedback = feedbackForAnswer(session.result, index);
    prefetchSpeech(feedback.improved_answer, "neutral", { audioId: `${session.id}-ielts-improved-${index}`, sessionId: session.id });
  });
}

/**
 * 生成一段本地录音的播放按钮。
 *
 * @param {string} audioId 本地录音 ID。
 * @param {string} label 按钮文字。
 * @returns {string} 播放按钮 HTML。
 */
function audioButton(audioId, label) {
  return `<button class="mini-button" type="button" data-action="play-audio" data-audio-id="${escapeHtml(audioId)}"><i data-lucide="play"></i>${escapeHtml(label)}</button>`;
}

/**
 * 生成日常评分小格。
 *
 * @param {string} label 分数名称。
 * @param {number} value 百分制分数。
 * @returns {string} 安全 HTML。
 */
function scoreCell(label, value) {
  return `<span><strong>${Number(value || 0)}</strong><small>${label}</small></span>`;
}

/**
 * 切换日常对话风格，并放弃仍使用旧风格的当前录音会话。
 *
 * @param {MouseEvent} event 风格按钮点击事件。
 * @returns {Promise<void>} 模式切换完成结果。
 */
async function handleDailyStyleChange(event) {
  const button = event.target.closest("button[data-value]");
  if (!button || button.dataset.value === dailyStyle) return;
  if (recorder.isRecording() && recordPurpose === "daily") {
    recorder.cancel();
    setRecordingUi(false);
    recordPurpose = "";
  }
  if (engine.active && engine.mode === "daily") await engine.stop();
  dailyStyle = button.dataset.value;
  selectSegment(elements.dailyStyle, dailyStyle);
}

/**
 * 选择 IELTS 模式并控制题卡来源设置。
 *
 * @param {MouseEvent} event 点击事件。
 * @returns {void}
 */
function handleIeltsModeChange(event) {
  const button = event.target.closest("button[data-value]");
  if (!button) return;
  ieltsMode = button.dataset.value;
  elements.ieltsMode.querySelectorAll("button").forEach((item) => item.classList.toggle("is-selected", item === button));
  elements.cardSourceRow.hidden = !["part2", "mock"].includes(ieltsMode);
}

/**
 * 从用户点击中预激活 iOS 语音播放并开始 IELTS 练习。
 *
 * @returns {Promise<void>} 练习启动结果。
 */
async function startIeltsFromUserGesture() {
  await primeAudioPlayback();
  await startIeltsPractice();
}

/**
 * 初始化选中的 IELTS 练习。
 *
 * @returns {Promise<void>} 练习准备结果。
 */
async function startIeltsPractice() {
  if (!requireGateway()) return;
  try {
    showPracticeView();
    showBusy("正在准备练习");
    await engine.start(ieltsMode, { cardSource });
  } catch (error) {
    hideBusy();
    resetIeltsView();
    showError(error);
  }
}

/**
 * 显示 IELTS 活动练习区域并清空旧结果。
 *
 * @returns {void}
 */
function showPracticeView() {
  elements.ieltsSetup.hidden = true;
  elements.ieltsResult.hidden = true;
  elements.ieltsPractice.hidden = false;
  elements.answerLog.replaceChildren();
}

/**
 * 渲染 PART2 题卡。
 *
 * @param {object} card 当前题卡。
 * @returns {void}
 */
function renderCueCard(card) {
  elements.practicePart.textContent = "PART 2";
  elements.practiceProgress.textContent = "准备";
  elements.cueCard.hidden = false;
  elements.cueTitle.textContent = card.title;
  elements.cuePoints.innerHTML = listItems(card.cue_points);
  elements.questionStage.hidden = true;
  elements.examRecordDock.hidden = true;
}

/**
 * 开始基于绝对截止时间的一分钟准备倒计时。
 *
 * @param {number} seconds 倒计时秒数。
 * @returns {void}
 */
function startCountdown(seconds) {
  window.clearInterval(countdownTimer);
  const total = Math.max(1, Number(seconds));
  const deadline = Date.now() + total * 1_000;
  elements.prepCountdown.hidden = false;
  const tick = () => {
    const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1_000));
    elements.countdownValue.textContent = String(remaining);
    elements.countdownProgress.style.transform = `scaleX(${remaining / total})`;
    if (remaining <= 0) finishCountdown();
  };
  tick();
  countdownTimer = window.setInterval(tick, 250);
}

/**
 * 结束准备倒计时并展示 PART2 陈述题。
 *
 * @returns {Promise<void>} 阶段推进结果。
 */
async function finishCountdown() {
  window.clearInterval(countdownTimer);
  countdownTimer = null;
  elements.prepCountdown.hidden = true;
  try {
    await engine.finishPart2Preparation();
  } catch (error) {
    showError(error);
  }
}

/**
 * 展示并自动播放考官问题，播放完成后按网页版规则自动开麦。
 *
 * @param {object} question 问题事件数据。
 * @returns {Promise<void>} 问题播报和自动开麦结果。
 */
async function presentExamQuestion(question) {
  const presentationId = ++questionPresentationId;

  // 1. 先展示题目；PART 2 陈述题不朗读题卡，倒计时结束后直接开麦。
  renderQuestion(question);
  if (!currentQuestionAudio) {
    if (question.kind === "part2_long") await startExamRecordingForQuestion(presentationId, question.text);
    return;
  }

  // 2. 播放期间锁定录音与重播按钮，避免把考官语音录进用户回答。
  elements.examRecord.disabled = true;
  elements.playQuestion.disabled = true;
  elements.examRecordLabel.textContent = "正在播放问题";
  try {
    await playAppAudio(currentQuestionAudio);
  } catch (error) {
    if (presentationId !== questionPresentationId) return;
    setExamRecorderReady();
    if (!isAudioPlaybackStoppedError(error)) showToast("未能自动播放问题，请阅读题目后点按回答", true);
    return;
  }

  // 3. 仅当用户仍停留在同一道题时自动开始录音。
  await startExamRecordingForQuestion(presentationId, question.text);
}

/**
 * 在问题仍有效时自动开始 IELTS 回答录音。
 *
 * @param {number} presentationId 当前问题展示序号。
 * @param {string} questionText 当前问题文本。
 * @returns {Promise<void>} 自动开麦结果。
 */
async function startExamRecordingForQuestion(presentationId, questionText) {
  if (presentationId !== questionPresentationId || activeView !== "ielts" || !engine.active || engine.currentQuestion !== questionText) return;
  if (recorder.isRecording()) return;
  elements.examRecord.disabled = false;
  elements.examRecordLabel.textContent = "正在打开麦克风";
  await toggleRecording("exam");
  if (!recorder.isRecording() && presentationId === questionPresentationId) setExamRecorderReady();
}

/**
 * 重播当前考官问题；重播后保留手动回答入口，不重复自动开麦。
 *
 * @returns {Promise<void>} 重播结果。
 */
async function replayCurrentQuestion() {
  if (!currentQuestionAudio) return;
  if (recorder.isRecording()) {
    showToast("请先结束当前回答再重播问题", true);
    return;
  }
  const presentationId = ++questionPresentationId;
  elements.examRecord.disabled = true;
  elements.playQuestion.disabled = true;
  elements.examRecordLabel.textContent = "正在播放问题";
  try {
    await playAppAudio(currentQuestionAudio);
  } catch (error) {
    if (!isAudioPlaybackStoppedError(error)) showError(error);
  } finally {
    if (presentationId === questionPresentationId && activeView === "ielts") setExamRecorderReady();
  }
}

/**
 * 恢复 IELTS 录音按钮的手动作答状态。
 *
 * @returns {void}
 */
function setExamRecorderReady() {
  elements.examRecord.disabled = false;
  elements.playQuestion.disabled = false;
  elements.examRecordLabel.textContent = "点按回答";
}

/**
 * 显示考官问题和可选语音按钮。
 *
 * @param {object} question 问题事件数据。
 * @returns {void}
 */
function renderQuestion(question) {
  elements.cueCard.hidden = question.part !== "part2";
  elements.prepCountdown.hidden = true;
  elements.questionStage.hidden = false;
  elements.examRecordDock.hidden = false;
  elements.practicePart.textContent = question.part.toUpperCase();
  elements.practiceProgress.textContent = `${question.index} / ${question.total}`;
  elements.questionSource.textContent = question.source === "dynamic_followup" ? "动态追问" : "考官问题";
  elements.questionText.textContent = question.text;
  currentQuestionAudio = question.audio || null;
  elements.playQuestion.hidden = !currentQuestionAudio;
  setExamRecorderReady();
  elements.helpSheet.hidden = true;
}

/**
 * 请求并显示当前问题提示。
 *
 * @returns {Promise<void>} 提示加载结果。
 */
async function requestAnswerHelp() {
  try {
    showBusy("正在准备提示");
    await engine.requestHelp();
  } catch (error) {
    hideBusy();
    showError(error);
  }
}

/**
 * 渲染关键词、句型和示例。
 *
 * @param {object} content 帮助内容。
 * @returns {void}
 */
function renderHelp(content) {
  elements.helpContent.innerHTML = [
    helpGroup("关键词", content.keywords),
    helpGroup("开头句", content.sentence_starters),
    helpGroup("短例句", content.sample_sentences),
  ].join("");
  elements.helpSheet.hidden = false;
}

/**
 * 生成一组答题帮助 HTML。
 *
 * @param {string} title 分组标题。
 * @param {Array<string>} values 内容。
 * @returns {string} 安全 HTML。
 */
function helpGroup(title, values = []) {
  return values.length ? `<h3>${title}</h3>${renderStringList(values)}` : "";
}

/**
 * 把一轮 IELTS 转写追加到答题记录。
 *
 * @param {object} answer 公开回答。
 * @returns {void}
 */
function renderAnswer(answer) {
  const item = document.createElement("article");
  item.className = "answer-item";
  item.innerHTML = `<span>${escapeHtml(answer.part.toUpperCase())} · ${Math.round(answer.durationSeconds)} 秒</span><p>${escapeHtml(answer.transcript || "未识别到文本")}</p>`;
  elements.answerLog.append(item);
}

/**
 * 恢复中断练习时重新显示已有文字回答。
 *
 * @param {Array<object>} answers 已恢复回答。
 * @returns {void}
 */
function renderRestoredAnswers(answers) {
  elements.answerLog.replaceChildren();
  answers.forEach(renderAnswer);
}

/**
 * 渲染最终 IELTS 分数与建议。
 *
 * @param {object} result 评分结果。
 * @returns {void}
 */
function renderAssessment(assessment) {
  const result = assessment.result || {};
  const session = assessment.session || { id: engine.sessionId, kind: "ielts", result, timings: assessment.timings, answers: assessment.answers, card: assessment.card };
  sessionLookup.set(session.id, session);
  elements.ieltsPractice.hidden = true;
  elements.ieltsSetup.hidden = true;
  elements.ieltsResult.hidden = false;
  elements.ieltsResult.dataset.sessionId = session.id;
  elements.overallBand.textContent = Number(result.overall_band || 0).toFixed(1);
  elements.scoreGrid.innerHTML = ieltsScoreGridMarkup(result.scores);
  elements.scoreReasons.innerHTML = listItems(result.score_reasons_zh);
  elements.scoreSuggestions.innerHTML = listItems(result.suggestions_zh);
  elements.improvedExamples.innerHTML = improvedExamplesMarkup(result.improved_examples);
  elements.timingGrid.innerHTML = timingCellsMarkup(session.timings);
  elements.resultCardReview.innerHTML = cueCardReviewMarkup(session.card, session.answers);
  elements.resultCardReview.closest(".result-section").hidden = !session.card;
  elements.resultTurns.innerHTML = ieltsTurnsMarkup(session);
  elements.startPart3.hidden = session.section !== "part2";
  refreshIcons();
  prefetchSessionSpeech(session);
}

/**
 * 生成 IELTS 四项评分网格。
 *
 * @param {object} scores IELTS 四项评分。
 * @returns {string} 评分网格 HTML。
 */
function ieltsScoreGridMarkup(scores = {}) {
  const labels = {
    fluency_coherence: "流利与连贯",
    lexical_resource: "词汇资源",
    grammatical_range_accuracy: "语法范围与准确性",
    pronunciation: "发音",
  };
  return Object.entries(labels).map(([key, label]) => `<div class="score-item"><strong>${Number(scores[key] || 0).toFixed(1)}</strong><span>${label}</span></div>`).join("");
}

/**
 * 生成 IELTS 改进表达列表。
 *
 * @param {unknown} examples 模型返回的改进表达。
 * @returns {string} 改进表达 HTML。
 */
function improvedExamplesMarkup(examples) {
  const values = normalizeStringList(examples);
  return values.length ? values.map((value) => `<div class="example-line">${escapeHtml(value)}</div>`).join("") : "<p class=\"empty-inline\">本次没有单独的改进表达。</p>";
}

/**
 * 生成 IELTS 各部分及总过程用时。
 *
 * @param {object} timings 本次练习计时数据。
 * @returns {string} 用时网格 HTML。
 */
function timingCellsMarkup(timings = {}) {
  const cells = Object.entries(timings.parts || {}).map(([part, seconds]) => [part.toUpperCase(), seconds]);
  cells.push(["实际作答", timings.answerSeconds || 0], ["总过程", timings.totalSeconds || 0]);
  return cells.map(([label, seconds]) => `<div class="timing-cell"><strong>${formatDuration(seconds)}</strong><span>${escapeHtml(label)}</span></div>`).join("");
}

/**
 * 生成 PART2 题卡与本次 PART3 实际问题回顾。
 *
 * @param {object|null} card 本次使用的题卡。
 * @param {Array<object>} answers 本次回答记录。
 * @returns {string} 题卡回顾 HTML。
 */
function cueCardReviewMarkup(card, answers = []) {
  if (!card) return "";
  const part3Answers = (answers || []).filter((answer) => answer.part === "part3");
  return `<div class="card-review-block"><small>PART2 题卡</small><strong>${escapeHtml(card.title || "")}</strong>${renderStringList(card.cue_points)}</div>
    <div class="card-review-block"><small>题卡预置 PART3 问题</small><ol>${listItems(card.part3_questions) || "<li>没有预置问题。</li>"}</ol></div>
    <div class="card-review-block"><small>本次实际提问</small><ol>${part3Answers.map((answer) => `<li>${escapeHtml(answer.question || "")}<span>${questionSourceLabel(answer.questionSource)}</span></li>`).join("") || "<li>本次结果中没有 PART3 提问。</li>"}</ol></div>`;
}

/**
 * 生成 IELTS 逐轮问题、回答、反馈和音频入口。
 *
 * @param {object} session IELTS 历史会话。
 * @returns {string} 逐轮结果 HTML。
 */
function ieltsTurnsMarkup(session) {
  const answers = Array.isArray(session.answers) ? session.answers : [];
  if (!answers.length) return "<p class=\"empty-inline\">本次没有可展示的逐轮回答。</p>";
  return answers.map((answer, index) => {
    const feedback = feedbackForAnswer(session.result, index);
    const questionAudioId = `${session.id}-${answer.id}-question`;
    const userAudioId = `${session.id}-${answer.id}-user`;
    const hasQuestionAudio = session.audioIds?.includes(questionAudioId);
    const hasUserAudio = session.audioIds?.includes(userAudioId);
    return `<details class="exam-turn-card" data-answer-index="${index}"${index < 2 ? " open" : ""}>
      <summary><strong>${escapeHtml(String(answer.part || session.section || "").toUpperCase())} · 第 ${index + 1} 轮</strong><span>${formatDuration(answer.durationSeconds)} · ${questionSourceLabel(answer.questionSource)}</span></summary>
      <div class="exam-turn-body">
        <section><div class="content-heading"><strong>考官问题</strong>${hasQuestionAudio ? audioButton(questionAudioId, "原声") : ""}</div><p>${escapeHtml(answer.question || "")}</p></section>
        <section><div class="content-heading"><strong>你的回答</strong>${hasUserAudio ? audioButton(userAudioId, "原声") : ""}</div><p>${escapeHtml(answer.transcript || "未识别到文本")}</p></section>
        <section><strong>本轮问题</strong>${renderStringList(feedback.problems_zh) || "<p>模型未指出单独问题。</p>"}</section>
        <section><strong>本轮建议</strong>${renderStringList(feedback.suggestions_zh) || "<p>继续保持完整作答。</p>"}</section>
        ${feedback.improved_answer ? `<section class="improved-box"><div class="content-heading"><strong>参考表达</strong><button class="mini-button" type="button" data-action="speak-ielts-improved" data-answer-index="${index}"><i data-lucide="volume-2"></i>发音</button></div><p>${escapeHtml(feedback.improved_answer)}</p></section>` : ""}
      </div>
    </details>`;
  }).join("");
}

/**
 * 把问题来源协议转换为中文标签。
 *
 * @param {string} source 问题来源协议值。
 * @returns {string} 中文来源。
 */
function questionSourceLabel(source) {
  return {
    preset: "题卡预置",
    model_generated: "模型生成",
    dynamic_followup: "动态追问",
    cue_card: "题卡陈述",
  }[source] || "流程预置";
}

/**
 * 从 PART2 评分页直接开始关联的 PART3 练习。
 *
 * @returns {Promise<void>} PART3 启动结果。
 */
async function startPart3FromResult() {
  await primeAudioPlayback();
  ieltsMode = "part3";
  selectSegment(elements.ieltsMode, ieltsMode);
  elements.cardSourceRow.hidden = true;
  await startIeltsPractice();
}

/**
 * 结束当前 IELTS 练习并返回选择页。
 *
 * @returns {Promise<void>} 停止结果。
 */
async function stopPractice() {
  if (!await confirmAction("结束本次练习", "当前尚未生成的评分不会保存。", "结束")) return;
  questionPresentationId += 1;
  stopAudioPlayback();
  if (recorder.isRecording()) recorder.cancel();
  await engine.stop();
  resetIeltsView();
}

/**
 * 重置 IELTS 页面但保留本地历史。
 *
 * @returns {void}
 */
function resetIeltsView() {
  questionPresentationId += 1;
  stopAudioPlayback();
  window.clearInterval(countdownTimer);
  countdownTimer = null;
  elements.ieltsSetup.hidden = false;
  elements.ieltsPractice.hidden = true;
  elements.ieltsResult.hidden = true;
  elements.answerLog.replaceChildren();
  elements.helpSheet.hidden = true;
}

/**
 * 处理历史筛选按钮。
 *
 * @param {MouseEvent} event 点击事件。
 * @returns {void}
 */
function handleHistoryFilter(event) {
  const button = event.target.closest("button[data-value]");
  if (!button) return;
  historyFilter = button.dataset.value;
  selectSegment(elements.historyFilter, historyFilter);
  renderHistory();
}

/**
 * 渲染本机练习历史及音频播放入口。
 *
 * @returns {Promise<void>} 历史加载结果。
 */
async function renderHistory() {
  const sessions = (await listSessions()).filter((session) => historyFilter === "all" || session.kind === historyFilter);
  if (!sessions.length) {
    elements.historyList.innerHTML = `<div class="empty-state"><i data-lucide="history"></i><strong>暂无练习记录</strong></div>`;
    refreshIcons();
    return;
  }
  elements.historyList.innerHTML = sessions.map((session) => {
    sessionLookup.set(session.id, session);
    const summary = session.kind === "daily"
      ? `${escapeHtml(session.result?.recognized_text || "")} · ${Number(session.result?.scores?.overall || 0)} 分`
      : `Overall ${Number(session.result?.overall_band || 0).toFixed(1)} · ${session.answers?.length || 0} 轮回答`;
    const detail = session.kind === "daily" ? dailyTurnBodyMarkup(session) : ieltsHistoryDetailMarkup(session);
    return `<details class="history-row" data-session-id="${escapeHtml(session.id)}">
      <summary><span class="history-label">${session.kind === "daily" ? "对话" : "IELTS"}</span><span class="history-title"><strong>${escapeHtml(session.title)}</strong><small>${summary}</small></span><time>${formatTime(session.createdAt)}</time><i data-lucide="chevron-down"></i></summary>
      <div class="history-detail">${detail}<div class="history-actions"><button class="icon-text-button" type="button" data-action="delete-session"><i data-lucide="trash-2"></i>删除本次记录</button></div></div>
    </details>`;
  }).join("");
  refreshIcons();
}

/**
 * 生成历史页中的完整 IELTS 结果详情。
 *
 * @param {object} session IELTS 历史会话。
 * @returns {string} IELTS 历史详情 HTML。
 */
function ieltsHistoryDetailMarkup(session) {
  const result = session.result || {};
  return `<div class="score-grid">${ieltsScoreGridMarkup(result.scores)}</div>
    <section class="result-section"><h2>评分依据</h2>${renderStringList(result.score_reasons_zh) || "<p>本次没有详细评分依据。</p>"}</section>
    <section class="result-section"><h2>调整建议</h2>${renderStringList(result.suggestions_zh) || "<p>本次没有单独的调整建议。</p>"}</section>
    <section class="result-section"><h2>改进表达</h2>${improvedExamplesMarkup(result.improved_examples)}</section>
    <section class="result-section"><h2>练习用时</h2><div class="timing-grid">${timingCellsMarkup(session.timings)}</div></section>
    ${session.card ? `<section class="result-section"><h2>题卡与讨论题</h2><div class="card-review">${cueCardReviewMarkup(session.card, session.answers)}</div></section>` : ""}
    <section class="result-section"><h2>逐轮记录</h2><div class="result-turns">${ieltsTurnsMarkup(session)}</div></section>`;
}

/**
 * 播放历史录音或删除单次历史。
 *
 * @param {MouseEvent} event 点击事件。
 * @returns {Promise<void>} 操作结果。
 */
async function handleHistoryAction(event) {
  const button = event.target.closest("button[data-action]");
  if (!button) {
    await handleSessionContentAction(event);
    return;
  }
  if (button.dataset.action !== "delete-session") {
    await handleSessionContentAction(event);
    return;
  }
  const sessionId = button.closest("[data-session-id]")?.dataset.sessionId;
  if (!sessionId || !await confirmAction("删除这次练习", "文字结果和关联录音都会从这台 iPhone 删除。", "删除")) return;
  await deleteSession(sessionId);
  clearSpeechAudioCache();
  sessionLookup.delete(sessionId);
  await Promise.all([renderHistory(), renderStorageSummary(), renderDailyConversation()]);
}

/**
 * 用本地设置填充服务商、录音和保留期限控件。
 *
 * @param {object} preferences 本地偏好。
 * @returns {void}
 */
function populatePreferences(preferences) {
  elements.textProvider.value = preferences.textProvider;
  elements.audioProvider.value = preferences.audioProvider;
  elements.transcriptionProvider.value = preferences.transcriptionProvider;
  elements.speechProvider.value = preferences.speechProvider;
  elements.saveRecordings.checked = preferences.saveRecordings;
  elements.retentionDays.value = String(preferences.retentionDays);
}

/**
 * 保存四类模型服务商选择。
 *
 * @returns {Promise<void>} 保存结果。
 */
async function saveProviderPreferences() {
  await savePreferences({
    textProvider: elements.textProvider.value,
    audioProvider: elements.audioProvider.value,
    transcriptionProvider: elements.transcriptionProvider.value,
    speechProvider: elements.speechProvider.value,
  });
  clearSpeechAudioCache();
  showToast("模型服务设置已保存");
}

/**
 * 保存录音开关和自动清理期限。
 *
 * @returns {Promise<void>} 保存与清理结果。
 */
async function saveLocalPreferences() {
  const preferences = await savePreferences({ saveRecordings: elements.saveRecordings.checked, retentionDays: Number(elements.retentionDays.value) });
  const removed = await pruneExpiredSessions(preferences.retentionDays);
  if (removed) clearSpeechAudioCache();
  if (removed) showToast(`已清理 ${removed} 条过期记录`);
  await Promise.all([renderStorageSummary(), ...(removed ? [renderHistory(), renderDailyConversation()] : [])]);
}

/**
 * 渲染本地数据数量和录音空间。
 *
 * @returns {Promise<void>} 统计加载结果。
 */
async function renderStorageSummary() {
  const summary = await getStorageSummary();
  elements.storageStats.innerHTML = `<div><strong>${summary.sessions}</strong><span>练习记录</span></div><div><strong>${summary.recordings}</strong><span>录音</span></div><div><strong>${formatBytes(summary.audioBytes)}</strong><span>音频空间</span></div>`;
}

/**
 * 删除全部录音但保留文字历史。
 *
 * @returns {Promise<void>} 清理结果。
 */
async function clearAllAudio() {
  const summary = await getStorageSummary();
  clearSpeechAudioCache();
  if (!summary.recordings) return showToast("当前没有已保存录音");
  if (!await confirmAction("清空全部录音", `将删除 ${summary.recordings} 段录音，文字和评分会保留。`, "清空录音")) return;
  await clearAudio();
  await Promise.all([renderStorageSummary(), renderHistory(), renderDailyConversation()]);
  showToast("录音已清空");
}

/**
 * 删除全部历史及关联录音。
 *
 * @returns {Promise<void>} 清理结果。
 */
async function clearAllHistory() {
  const summary = await getStorageSummary();
  if (!summary.sessions) return showToast("当前没有练习历史");
  if (!await confirmAction("清空练习历史", `将删除 ${summary.sessions} 条记录和 ${summary.recordings} 段关联录音。`, "全部删除")) return;
  await clearHistory();
  clearSpeechAudioCache();
  elements.conversation.querySelectorAll(".conversation-turn").forEach((item) => item.remove());
  elements.conversationEmpty.hidden = false;
  await Promise.all([renderStorageSummary(), renderHistory()]);
  showToast("练习历史已清空");
}

/**
 * 恢复题库、提示词和练习数据，保留激活信息。
 *
 * @returns {Promise<void>} 恢复结果。
 */
async function resetAllLocalData() {
  if (!await confirmAction("恢复本地数据", "自定义题库、提示词、练习历史和录音都会删除，激活信息会保留。", "恢复默认")) return;
  await factoryReset({ clearAuth: false });
  clearSpeechAudioCache();
  const preferences = await getPreferences();
  populatePreferences(preferences);
  elements.conversation.querySelectorAll(".conversation-turn").forEach((item) => item.remove());
  elements.conversationEmpty.hidden = false;
  await Promise.all([renderPrompts(), renderQuestionBank(), renderStorageSummary(), renderHistory()]);
  showToast("本地数据已恢复默认");
}

/**
 * 渲染十组本地提示词编辑器。
 *
 * @returns {Promise<void>} 提示词加载结果。
 */
async function renderPrompts() {
  const prompts = await getPrompts();
  elements.promptFields.innerHTML = Object.entries(PROMPT_LABELS).map(([id, label]) => `<label>${escapeHtml(label)}<textarea name="${id}" rows="4" required>${escapeHtml(prompts[id])}</textarea></label>`).join("");
}

/**
 * 保存提示词表单。
 *
 * @param {SubmitEvent} event 表单事件。
 * @returns {Promise<void>} 保存结果。
 */
async function savePromptEditor(event) {
  event.preventDefault();
  const form = new FormData(elements.promptsForm);
  await savePrompts(Object.fromEntries(form.entries()));
  showToast("提示词已保存在这台 iPhone");
}

/**
 * 删除提示词改动并恢复默认值。
 *
 * @returns {Promise<void>} 恢复结果。
 */
async function resetPromptEditor() {
  if (!await confirmAction("恢复默认提示词", "当前十组自定义内容会被默认值替换。", "恢复")) return;
  await resetPrompts();
  await renderPrompts();
  showToast("提示词已恢复默认");
}

/**
 * 渲染可搜索、启停和删除的本地题库。
 *
 * @returns {Promise<void>} 题库加载结果。
 */
async function renderQuestionBank() {
  const [cards, allCards] = await Promise.all([listCards({ search: elements.bankSearch.value }), listCards()]);
  elements.bankCount.textContent = `${allCards.length} 道题卡`;
  elements.bankList.innerHTML = cards.map((card) => `<article class="bank-row" data-card-id="${escapeHtml(card.id)}">
    <strong>${escapeHtml(card.title)}</strong><span>${escapeHtml(card.category)} · ${card.source === "builtin" ? "内置" : "自定义"}</span>
    <div class="bank-row-actions"><button type="button" data-action="toggle-card">${card.enabled ? "停用" : "启用"}</button>${card.source === "builtin" ? "" : `<button type="button" data-action="edit-card">编辑</button><button type="button" data-action="delete-card">删除</button>`}</div>
  </article>`).join("");
}

/**
 * 处理题卡启停、编辑和删除。
 *
 * @param {MouseEvent} event 点击事件。
 * @returns {Promise<void>} 操作结果。
 */
async function handleCardAction(event) {
  const button = event.target.closest("button[data-action]");
  if (!button) return;
  const cardId = button.closest("[data-card-id]")?.dataset.cardId;
  const card = (await listCards()).find((item) => item.id === cardId);
  if (!card) return;
  if (button.dataset.action === "toggle-card") await setCardEnabled(cardId, !card.enabled);
  if (button.dataset.action === "edit-card") openCardEditor(card);
  if (button.dataset.action === "delete-card") {
    if (!await confirmAction("删除题卡", `“${card.title}”将从这台 iPhone 永久删除。`, "删除")) return;
    await deleteCard(cardId);
  }
  await Promise.all([renderQuestionBank(), renderStorageSummary()]);
}

/**
 * 打开新增或编辑题卡对话框。
 *
 * @param {object|null} card 待编辑题卡；空值表示新增。
 * @returns {void}
 */
function openCardEditor(card = null) {
  elements.cardForm.reset();
  elements.cardId.value = card?.id || "";
  elements.cardDialogTitle.textContent = card ? "编辑题卡" : "新增题卡";
  elements.cardTitle.value = card?.title || "";
  elements.cardPoints.value = card?.cue_points?.join("\n") || "";
  elements.cardRounding.value = card?.rounding_question || "";
  elements.cardTopic.value = card?.part3_topic || "";
  elements.cardQuestions.value = card?.part3_questions?.join("\n") || "";
  elements.cardCategory.value = card?.category || "custom";
  elements.cardDialog.showModal();
}

/**
 * 关闭题卡编辑对话框。
 *
 * @returns {void}
 */
function closeCardEditor() {
  elements.cardDialog.close();
}

/**
 * 校验并保存题卡编辑内容。
 *
 * @param {SubmitEvent} event 表单事件。
 * @returns {Promise<void>} 保存结果。
 */
async function submitCardEditor(event) {
  event.preventDefault();
  const cuePoints = elements.cardPoints.value.split("\n").map((line) => line.trim()).filter(Boolean);
  const questions = elements.cardQuestions.value.split("\n").map((line) => line.trim()).filter(Boolean);
  if (cuePoints.length < 3 || cuePoints.length > 4) return showToast("提示点需要 3 到 4 条", true);
  if (questions.length < 4 || questions.length > 8) return showToast("PART3 问题需要 4 到 8 条", true);
  await saveCard({
    id: elements.cardId.value || undefined,
    title: elements.cardTitle.value.trim(),
    cue_points: cuePoints,
    rounding_question: elements.cardRounding.value.trim(),
    part3_topic: elements.cardTopic.value.trim(),
    part3_questions: questions,
    category: elements.cardCategory.value.trim(),
    tags: [elements.cardCategory.value.trim()],
    source: "custom",
    enabled: true,
  });
  closeCardEditor();
  await Promise.all([renderQuestionBank(), renderStorageSummary()]);
  showToast("题卡已保存");
}

/**
 * 导入 JSON 题卡并分配新的本地 ID。
 *
 * @returns {Promise<void>} 导入结果。
 */
async function importQuestionBank() {
  const file = elements.bankFile.files?.[0];
  elements.bankFile.value = "";
  if (!file) return;
  try {
    const parsed = JSON.parse(await file.text());
    if (!Array.isArray(parsed)) throw new Error("题库文件必须是 JSON 数组");
    const cards = parsed.map((card) => {
      if (!card?.title || !Array.isArray(card.cue_points) || card.cue_points.length < 3 || !Array.isArray(card.part3_questions) || card.part3_questions.length < 4) throw new Error("题库中存在字段不完整的题卡");
      return { ...card, id: `custom-${crypto.randomUUID()}`, source: "custom" };
    });
    await saveCards(cards);
    await Promise.all([renderQuestionBank(), renderStorageSummary()]);
    showToast(`已导入 ${cards.length} 道题卡`);
  } catch (error) {
    showError(error);
  }
}

/**
 * 清除自定义题卡并恢复 60 道内置题卡。
 *
 * @returns {Promise<void>} 重置结果。
 */
async function restoreQuestionBank() {
  if (!await confirmAction("重置题库", "自定义题卡会被删除，内置题卡恢复为全部启用。", "重置")) return;
  await resetQuestionBank();
  await Promise.all([renderQuestionBank(), renderStorageSummary()]);
  showToast("题库已恢复默认");
}

/**
 * iOS 进入后台时停止录音并保存当前文字练习状态。
 *
 * @returns {Promise<void>} 状态保存结果。
 */
async function handleVisibilityChange() {
  if (document.visibilityState !== "hidden") return;
  questionPresentationId += 1;
  stopAudioPlayback();
  if (recorder.isRecording()) {
    setRecordingUi(false);
    recordPurpose = "";
  }
  recorder.release();
  await engine.persistActiveState();
}

/**
 * 页面关闭时释放仍被复用的麦克风流。
 *
 * @returns {void}
 */
function releaseRecorderResources() {
  questionPresentationId += 1;
  stopAudioPlayback();
  recorder.release();
}

/**
 * 显示确认对话框并等待用户选择。
 *
 * @param {string} title 标题。
 * @param {string} message 影响范围。
 * @param {string} actionLabel 确认按钮文字。
 * @returns {Promise<boolean>} 是否确认。
 */
function confirmAction(title, message, actionLabel) {
  elements.confirmTitle.textContent = title;
  elements.confirmMessage.textContent = message;
  elements.confirmAction.textContent = actionLabel;
  elements.confirmDialog.showModal();
  return new Promise((resolve) => {
    elements.confirmDialog.addEventListener("close", () => resolve(elements.confirmDialog.returnValue === "confirm"), { once: true });
  });
}

/**
 * 显示阻止重复提交的处理层。
 *
 * @param {string} copy 当前处理步骤。
 * @returns {void}
 */
function showBusy(copy) {
  elements.busyCopy.textContent = copy;
  elements.busyOverlay.hidden = false;
}

/**
 * 隐藏处理层。
 *
 * @returns {void}
 */
function hideBusy() {
  elements.busyOverlay.hidden = true;
}

/**
 * 在处理层中执行一个异步任务。
 *
 * @param {string} copy 处理文案。
 * @param {Function} action 异步任务。
 * @returns {Promise<void>} 任务结果。
 */
async function runBusy(copy, action) {
  showBusy(copy);
  try {
    await action();
  } catch (error) {
    showError(error);
  } finally {
    hideBusy();
  }
}

/**
 * 显示短暂状态消息。
 *
 * @param {string} message 消息文本。
 * @param {boolean} isError 是否为错误。
 * @returns {void}
 */
function showToast(message, isError = false) {
  const toast = document.createElement("div");
  toast.className = `toast${isError ? " is-error" : ""}`;
  toast.textContent = message;
  elements.toastRegion.replaceChildren(toast);
  window.setTimeout(() => toast.remove(), 3_200);
}

/**
 * 将未知异常展示为用户可理解的消息。
 *
 * @param {unknown} error 异常。
 * @returns {void}
 */
function showError(error) {
  hideBusy();
  if (error?.status === 401) {
    gatewayNeedsActivation = true;
    setGatewayState(false, "需要重新激活");
    if (error?.code === "activation_required") switchView("data");
  }
  showToast(error instanceof Error ? error.message : "操作失败，请重试", true);
}

/**
 * 生成字符串数组的安全列表 HTML。
 *
 * @param {unknown} values 字符串或字符串数组。
 * @param {string} className 可选列表类名。
 * @returns {string} 安全 HTML。
 */
function renderStringList(values, className = "") {
  const normalized = normalizeStringList(values);
  return normalized.length ? `<ul class="${className}">${listItems(normalized)}</ul>` : "";
}

/**
 * 生成安全列表项 HTML。
 *
 * @param {unknown} values 字符串或字符串数组。
 * @returns {string} 安全 HTML。
 */
function listItems(values) {
  return normalizeStringList(values).map((value) => `<li>${escapeHtml(value)}</li>`).join("");
}

/**
 * 转义需要插入模板的用户或模型文本。
 *
 * @param {unknown} value 原始值。
 * @returns {string} HTML 安全文本。
 */
function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[character]));
}

/**
 * 格式化北京时间风格的本机显示时间。
 *
 * @param {string} isoTime ISO 时间。
 * @returns {string} 月日和时分。
 */
function formatTime(isoTime) {
  return new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(isoTime));
}

/**
 * 把秒数格式化为分秒展示。
 *
 * @param {number} seconds 时长秒数。
 * @returns {string} m:ss 格式时长。
 */
function formatDuration(seconds) {
  const safeSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(2, "0")}`;
}

/**
 * 把字节数转换为紧凑存储单位。
 *
 * @param {number} bytes 字节数。
 * @returns {string} B、KB 或 MB 文本。
 */
function formatBytes(bytes) {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

/**
 * 注册 PWA Service Worker；本地非安全环境不执行。
 *
 * @returns {void}
 */
function registerServiceWorker() {
  if ("serviceWorker" in navigator && (location.protocol === "https:" || location.hostname === "localhost" || location.hostname === "127.0.0.1")) {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  }
}

initialize().catch(showError);
